import { useEffect, useState, useCallback } from 'react';

/**
 * pre180 (Punkt 7, passiv): Geräte-Health-Check OHNE die Kamera zu öffnen.
 *
 * Erkennt nur die zuverlässig passiv feststellbaren Fälle:
 *   - „blockiert" (Berechtigung im Browser verweigert) via Permissions-API
 *   - „fehlt"     (kein Kamera-/Mikrofon-Gerät vorhanden) via enumerateDevices
 *
 * Es wird KEIN getUserMedia ausgeführt → keine Berechtigungs-Popups, keine Kamera-LED.
 * „belegt durch andere App" lässt sich passiv NICHT erkennen und wird bewusst nicht
 * gemeldet (das fällt beim Annehmen eines Calls bzw. im Geräte-Check auf).
 *
 * Sofort-Aktualisierung: Permissions-`onchange` (Entzug/Erteilung), `devicechange`
 * (Ein-/Ausstecken), Sichtbarkeitswechsel und ein leichter Intervall-Fallback.
 */
export type DevState = 'ok' | 'prompt' | 'denied' | 'missing' | 'unknown';

export interface DeviceHealth {
  camera: DevState;
  microphone: DevState;
  overall: 'ok' | 'warn' | 'bad';
}

const isBad = (s: DevState) => s === 'denied' || s === 'missing';
const isWarn = (s: DevState) => s === 'prompt' || s === 'unknown';

function overallOf(a: DevState, b: DevState): DeviceHealth['overall'] {
  if (isBad(a) || isBad(b)) return 'bad';
  if (isWarn(a) || isWarn(b)) return 'warn';
  return 'ok';
}

async function queryPerm(name: string): Promise<PermissionState | null> {
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state;
  } catch {
    return null;
  }
}

export function useDeviceHealth(): DeviceHealth {
  const [health, setHealth] = useState<DeviceHealth>({ camera: 'unknown', microphone: 'unknown', overall: 'ok' });

  const check = useCallback(async () => {
    let camCount = 0;
    let micCount = 0;
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      camCount = devs.filter((d) => d.kind === 'videoinput').length;
      micCount = devs.filter((d) => d.kind === 'audioinput').length;
    } catch {
      /* ignore */
    }

    const camPerm = await queryPerm('camera');
    const micPerm = await queryPerm('microphone');

    const base = (perm: PermissionState | null, count: number): DevState => {
      if (perm === 'denied') return 'denied';   // im Browser blockiert
      if (count === 0) return 'missing';         // kein Gerät vorhanden
      if (perm === 'granted') return 'ok';
      if (perm === 'prompt') return 'prompt';    // noch nicht freigegeben (kein Alarm)
      return 'ok';
    };

    const camera = base(camPerm, camCount);
    const microphone = base(micPerm, micCount);
    setHealth({ camera, microphone, overall: overallOf(camera, microphone) });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = () => { if (!cancelled) void check(); };
    run();

    const md = navigator.mediaDevices;
    md?.addEventListener?.('devicechange', run);
    const onVis = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVis);

    const statuses: PermissionStatus[] = [];
    void (async () => {
      for (const name of ['camera', 'microphone']) {
        try {
          const st = await navigator.permissions.query({ name: name as PermissionName });
          st.onchange = run;
          statuses.push(st);
        } catch {
          /* ignore */
        }
      }
    })();

    const iv = window.setInterval(run, 20000);

    return () => {
      cancelled = true;
      md?.removeEventListener?.('devicechange', run);
      document.removeEventListener('visibilitychange', onVis);
      statuses.forEach((s) => { try { s.onchange = null; } catch { /* */ } });
      window.clearInterval(iv);
    };
  }, [check]);

  return health;
}
